import type { NavigateFunction } from "react-router-dom";
import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import {
  AgentAuditOutcome,
  ContractStateDto,
  type EventListItemDto,
  OrganizationType,
} from "../../autogen/bff-api";
import { urls } from "../../urls";
import type {
  AgentToolDescriptor,
  AgentTurn,
  ConfirmDecision,
  DocumentSearchScope,
  DocumentSource,
  EntityHighlight,
  IndexedEntityKind,
  RiskTier,
} from "./agentTypes";
import type { PendingConfirmation } from "./ConfirmationCard";
import { currentToolName } from "./retiredToolNames";
import {
  addEntityHighlight,
  highlightFromMutationResult,
  highlightFromReadResult,
  isPlainRecord,
} from "./entityHighlights";
import { OPEN_DOCUMENT_TOOL_NAME, SEARCH_TOOL_NAME, runTool } from "./tools";
import type { ToolContext, ToolResult } from "./tools";
import type { AuditProps } from "./useAgentAudit";
import type { DocumentSearch } from "./useDocumentSearch";

export type StepOutcome = "stream" | "navigate" | "aborted";

// The effect a single executed call has on the turn: its result (always fed back to the planner), an
// optional outcome transition (null leaves the current outcome untouched), and whether it ends the
// tool loop.
export type CallResult = { result: ToolResult; outcome: StepOutcome | null; breakOuter: boolean };

export type CallDeps = {
  ctx: ToolContext;
  entityIndex: Map<string, Record<string, unknown>>;
  navigate: NavigateFunction;
  navigateToEvent: (event: EventListItemDto) => void;
  searchDocuments: DocumentSearch;
  openDocument: (documentId: string, name: string) => Promise<{ result: ToolResult; source: DocumentSource | null }>;
  generateDraft: (args: Record<string, unknown>) => Promise<ToolResult>;
  recordAudit: (outcome: AgentAuditOutcome, p: AuditProps) => void;
  waitForConfirmation: (pending: PendingConfirmation) => Promise<ConfirmDecision>;
  setActiveStep: (label: string | null) => void;
  updateTurn: (patch: (turn: AgentTurn) => AgentTurn) => void;
  turnIndex: number;
  userInstruction: string;
  seenCalls: Set<string>;
};

const DECLINED: ToolResult = { ok: false, deviationCode: "USER_DECLINED", message: "Brukeren avviste handlingen." };

// A successfully created entity whose page the assistant should open right away, keyed by tool.
const RESULT_NAV: Record<string, ((id: string) => string) | undefined> = {
  createSourcingEvent: (id) => {
    const { pathname, search } = urls.app.sourcingEvents.edit.go(id).info;
    return `${pathname}?${search}`;
  },
};

// The only fact resolveEntityNavigation is allowed to route on: an explicit tag this module stamped
// on an entity at index time. Never re-derived from an entity's own fields downstream — see
// resolveEntityNavigation's doc comment for why (EventRevisionDto vs ContractDto collide on `state`).
const AGENT_ENTITY_KIND = "__agentEntityKind";

// ContractDto and ContractListItemDto are the only response shapes with a bare (no `contractId`
// sibling) `id` that combine a `state: ContractStateDto` with a required top-level `currency`.
// EventRevisionDto — nested inside SourcingEventDto.changes and walked by indexEntities' array
// recursion — also has `id` + `state` (its EventRevisionState values literally overlap
// ContractStateDto's: "Draft"/"Published"), but never a top-level `currency`, so it's excluded here
// rather than mis-tagged as a contract.
const isBareContractShape = (obj: Record<string, unknown>): boolean =>
  typeof obj.state === "string" && typeof obj.currency === "string";

// A list item carries a top-level `title` — the one shape every other consumer (navigation, card
// label) is written against. `getSourcingEvent` nests its title under `eventData`, so tag a copy onto
// that same shape here and nothing downstream has to special-case it. Returns null for anything that
// isn't recognizably a sourcing event.
const asIndexedEvent = (obj: Record<string, unknown>): Record<string, unknown> | null => {
  const eventData = obj.eventData;
  if (isPlainRecord(eventData) && typeof eventData.title === "string") {
    return { ...obj, title: eventData.title, [AGENT_ENTITY_KIND]: "v2Event" };
  }
  return null;
};

// The one org payload a declared tool kind can't cover: searchOrganizationsAndEntries returns a
// MIXED list, so each hit carries its own explicit type discriminator instead. Full-profile reads
// route via their tool's declared kind.
const asOrganizationKind = (obj: Record<string, unknown>): IndexedEntityKind | null => {
  if (obj.type === OrganizationType.Organization) {
    return "organization";
  }
  if (obj.type === OrganizationType.OrganizationEntry) {
    return "orgEntry";
  }
  return null;
};

const indexEntity = (
  obj: Record<string, unknown>,
  into: Map<string, Record<string, unknown>>,
  declaredKind: IndexedEntityKind | undefined,
): void => {
  const event = asIndexedEvent(obj);
  if (event && typeof event.id === "string") {
    into.set(event.id, event);
    return;
  }
  if (typeof obj.id === "string") {
    const kind = declaredKind ?? asOrganizationKind(obj) ?? (isBareContractShape(obj) ? "contract" : null);
    into.set(obj.id, kind ? { ...obj, [AGENT_ENTITY_KIND]: kind } : obj);
  }
  if (typeof obj.bidId === "string") {
    into.set(obj.bidId, obj);
  }
  if (typeof obj.contractId === "string" && typeof obj.state === "string") {
    into.set(obj.contractId, { ...obj, [AGENT_ENTITY_KIND]: "contract" });
  }
};

// Exported for direct unit testing of the tagging rules above — indexEntity/asIndexedEvent are the
// part of the pipeline actually under test; runAndIndex just wires this to a live tool call.
// declaredKind is the producing tool's entity kind (server-owned, from the allowlist): it applies to
// the result's primary entities — the root object, or the items of a list wrapper — never to
// collections nested INSIDE an entity (an org's contactPersons are not orgs).
export const indexEntities = (
  data: unknown,
  into: Map<string, Record<string, unknown>>,
  declaredKind?: IndexedEntityKind,
): void => {
  if (Array.isArray(data)) {
    data.forEach((item) => indexEntities(item, into, declaredKind));
    return;
  }
  if (!data || typeof data !== "object") {
    return;
  }
  const obj = data as Record<string, unknown>;
  indexEntity(obj, into, declaredKind);
  const nestedKind = typeof obj.id === "string" ? undefined : declaredKind;
  Object.values(obj).forEach((value) => {
    if (Array.isArray(value)) {
      indexEntities(value, into, nestedKind);
    }
  });
};

const runAndIndex = async (deps: CallDeps, callName: string, args: Record<string, unknown>): Promise<ToolResult> => {
  const result = await runTool(callName, args, deps.ctx, deps.generateDraft);
  if (result.ok) {
    const entityKind = agentToolCatalog.find((tool) => tool.toolName === callName)?.entityKind;
    indexEntities(result.data, deps.entityIndex, entityKind);
  }
  return result;
};

export type EntityNavigationDeps = Pick<CallDeps, "entityIndex" | "navigate" | "navigateToEvent">;

// The routing rule for an indexed entity id, shared by the planner's own "navigate" tool call and the
// entity highlight cards a turn renders. Dispatches ONLY on the `__agentEntityKind` tag indexEntity
// stamped at index time — never by re-inspecting the entity's own fields (an EventRevisionDto and a
// ContractDto both carry `id` + a `state` string that can literally be "Draft"/"Published"; guessing
// from shape alone routed a revision to a contract 404). Fails closed — returns null, never a guess —
// for an id the index has never seen or an entity indexEntity chose not to tag, so callers never route
// to a 404 on a type we don't actually recognize.
export const resolveEntityNavigation = (id: string, deps: EntityNavigationDeps): (() => void) | null => {
  if (!id) {
    return null;
  }
  const entity = deps.entityIndex.get(id);
  if (!entity) {
    return null;
  }
  const kind = entity[AGENT_ENTITY_KIND];
  if (kind === "v2Event") {
    return () => deps.navigateToEvent(entity as unknown as EventListItemDto);
  }
  if (kind === "organization") {
    return () => deps.navigate(urls.app.organizationProfile.go(id));
  }
  if (kind === "orgEntry") {
    return () => deps.navigate(urls.app.organizationProfile.goToEntry(id));
  }
  if (kind === "project") {
    return () => deps.navigate(urls.app.projects.view.go(id));
  }
  if (kind === "need") {
    return () => deps.navigate(urls.app.needs.view.go(id));
  }
  if (kind === "me") {
    return () => deps.navigate(urls.app.profile);
  }
  if (kind === "notification") {
    return () => deps.navigate(urls.app.notifications);
  }
  if (kind !== "contract") {
    return null;
  }
  const contractId = typeof entity.contractId === "string" ? entity.contractId : id;
  return () =>
    deps.navigate(
      entity.state === ContractStateDto.Draft
        ? urls.contracts.edit.go(contractId).details
        : urls.contracts.viewSingle.go(contractId),
    );
};

// A highlight card is only worth rendering if clicking it actually goes somewhere — see
// resolveEntityNavigation's fail-closed contract. Dropping the ones that don't resolve is strictly
// better than rendering a card that 404s.
export const navigableHighlight = (
  highlight: EntityHighlight | null,
  deps: EntityNavigationDeps,
): EntityHighlight | null => (highlight && resolveEntityNavigation(highlight.id, deps) ? highlight : null);

const SEARCH_STEP_LABEL = "Søker i dokumentene";
const OPEN_DOCUMENT_STEP_LABEL = "Henter dokumentet";

const sourceKey = (source: DocumentSource): string => source.memoryId ?? source.documentId ?? source.documentName;

const mergeSources = (existing: DocumentSource[], added: DocumentSource[]): DocumentSource[] => {
  const seen = new Set(existing.map(sourceKey));
  return [...existing, ...added.filter((source) => !seen.has(sourceKey(source)))];
};

const openDocumentCall = async (args: Record<string, unknown>, deps: CallDeps): Promise<CallResult> => {
  const documentId = String(args.documentId ?? "");
  const name = String(args.name ?? "");
  deps.setActiveStep(OPEN_DOCUMENT_STEP_LABEL);
  const { result, source } = await deps.openDocument(documentId, name);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: OPEN_DOCUMENT_STEP_LABEL, ok: result.ok }],
    sources: source ? mergeSources(turn.sources ?? [], [source]) : turn.sources,
  }));
  return { result, outcome: null, breakOuter: false };
};

const searchCall = async (args: Record<string, unknown>, deps: CallDeps): Promise<CallResult> => {
  const query = String(args.query ?? "");
  const scope: DocumentSearchScope = args.scope === "page" ? "page" : args.scope === "bids" ? "bids" : "organization";
  const callKey = `${SEARCH_TOOL_NAME}:${JSON.stringify({ query, scope })}`;
  if (deps.seenCalls.has(callKey)) {
    return { result: { ok: true, data: { note: "Samme søk allerede utført." } }, outcome: null, breakOuter: false };
  }
  deps.setActiveStep(SEARCH_STEP_LABEL);
  const { result, sources } = await deps.searchDocuments(query, scope);
  if (result.ok) {
    deps.seenCalls.add(callKey);
  }
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label: SEARCH_STEP_LABEL, ok: result.ok }],
    sources: mergeSources(turn.sources ?? [], sources),
  }));
  return { result, outcome: null, breakOuter: false };
};

const navigateCall = (args: Record<string, unknown>, deps: CallDeps): CallResult => {
  const targetId = typeof args.id === "string" ? args.id : "";
  const go = targetId ? resolveEntityNavigation(targetId, deps) : null;
  let navigated = true;
  if (go) {
    go();
  } else if (typeof args.route === "string" && args.route) {
    deps.navigate(args.route);
  } else {
    navigated = false;
  }
  deps.updateTurn((turn) => ({ ...turn, note: navigated ? "Åpner siden." : "Fant ikke siden å åpne." }));
  return { result: { ok: true, data: { navigated } }, outcome: "navigate", breakOuter: true };
};

const gatedMutationCall = async (
  input: {
    callName: string;
    args: Record<string, unknown>;
    label: string;
    riskTier: RiskTier;
    descriptor: AgentToolDescriptor;
    plannerSummary: string | null;
  },
  deps: CallDeps,
): Promise<CallResult> => {
  const { callName, args, label, riskTier, descriptor, plannerSummary } = input;
  const audit = (outcome: AgentAuditOutcome, auditedArgs: Record<string, unknown>, deviationCode?: string) =>
    deps.recordAudit(outcome, {
      toolName: callName,
      riskTier,
      args: auditedArgs,
      turnId: deps.turnIndex,
      userInstruction: deps.userInstruction,
      deviationCode,
    });

  audit(AgentAuditOutcome.Proposed, args);
  const decision = await deps.waitForConfirmation({ label, riskTier, descriptor, args, summary: plannerSummary });
  if (decision.kind === "supersede") {
    audit(AgentAuditOutcome.Rejected, args);
    deps.updateTurn((turn) => ({ ...turn, note: "Justerer forslaget …" }));
    return { result: DECLINED, outcome: "aborted", breakOuter: true };
  }
  if (decision.kind === "cancel") {
    audit(AgentAuditOutcome.Rejected, args);
    deps.updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label: `${label} – avbrutt`, ok: false }] }));
    return { result: DECLINED, outcome: null, breakOuter: true };
  }
  audit(AgentAuditOutcome.Confirmed, decision.finalArgs);
  deps.setActiveStep(label);
  const result = await runAndIndex(deps, callName, decision.finalArgs);
  const mutationHighlight = highlightFromMutationResult(result, decision.finalArgs, deps.entityIndex);
  const highlight = navigableHighlight(mutationHighlight, deps);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label, ok: result.ok }],
    entities: addEntityHighlight(turn.entities ?? [], highlight),
  }));
  audit(
    result.ok ? AgentAuditOutcome.Executed : AgentAuditOutcome.Failed,
    decision.finalArgs,
    result.ok ? undefined : result.deviationCode,
  );
  const navTo = RESULT_NAV[callName];
  const created = navTo && result.ok ? (result.data as { id?: unknown }) : undefined;
  if (navTo && typeof created?.id === "string") {
    deps.navigate(navTo(created.id));
    deps.updateTurn((turn) => ({ ...turn, note: "Opprettet — åpner den." }));
    return { result, outcome: "navigate", breakOuter: true };
  }
  return { result, outcome: "stream", breakOuter: true };
};

const readCall = async (
  input: { callName: string; args: Record<string, unknown>; label: string },
  deps: CallDeps,
): Promise<CallResult> => {
  const { callName, args, label } = input;
  const callKey = `${callName}:${JSON.stringify(args)}`;
  if (deps.seenCalls.has(callKey)) {
    return { result: { ok: true, data: { note: "Samme kall allerede utført." } }, outcome: null, breakOuter: false };
  }
  deps.seenCalls.add(callKey);
  deps.setActiveStep(label);
  const result = await runAndIndex(deps, callName, args);
  const highlight = navigableHighlight(highlightFromReadResult(result, deps.entityIndex), deps);
  deps.updateTurn((turn) => ({
    ...turn,
    steps: [...turn.steps, { label, ok: result.ok }],
    entities: addEntityHighlight(turn.entities ?? [], highlight),
  }));
  return { result, outcome: null, breakOuter: false };
};

// Dispatch one planner-requested call by kind: client-side navigation, a gated (confirmed) mutation,
// or a read. Memory bookkeeping (the tool reply, the SKIPPED balancing) is the loop's job — this
// Tool descriptions are planner-facing documentation and can run to a paragraph (updateContract
// enumerates every field); a status label is their first clause.
export const toolStepLabel = (description: string | undefined, callName: string): string => {
  if (!description) {
    return callName;
  }
  const clauseEnd = [":", "."]
    .map((stop) => description.indexOf(stop))
    .filter((index) => index !== -1)
    .sort((a, b) => a - b)[0];
  return clauseEnd === undefined ? description : description.slice(0, clauseEnd);
};

// only performs the call and reports its effect on the turn.
export const executeCall = async (
  requestedName: string,
  args: Record<string, unknown>,
  deps: CallDeps,
  plannerSummary: string | null = null,
): Promise<CallResult> => {
  const callName = currentToolName(requestedName);
  if (callName === "navigate") {
    return navigateCall(args, deps);
  }
  if (callName === SEARCH_TOOL_NAME) {
    return searchCall(args, deps);
  }
  if (callName === OPEN_DOCUMENT_TOOL_NAME) {
    return openDocumentCall(args, deps);
  }
  const descriptor = agentToolCatalog.find((tool) => tool.toolName === callName);
  const label = toolStepLabel(descriptor?.description, callName);
  const riskTier: RiskTier = descriptor?.riskTier ?? "none";
  if (descriptor && riskTier !== "none") {
    return gatedMutationCall({ callName, args, label, riskTier, descriptor, plannerSummary }, deps);
  }
  return readCall({ callName, args, label }, deps);
};
